import { Button, Grid, Group, Radio, Stack, Textarea, TextInput } from '@mantine/core'
import type { UseFormReturnType } from '@mantine/form'
import { useHotkeys } from '@mantine/hooks'
import type { AssetTargetSchema } from '@opsopti/shared'
import {
  assetTargetSchema,
  castAssetTargetToForm,
  isFieldsListValid,
  useAssetTargetCreateMutation,
  useAssetTargetForm,
  useAssetTargetQuery,
  useAssetTargetUpdateMutation,
  useGlobalState,
} from '@opsopti/shared'
import type { CreateAssetTargetResponse, UpdateAssetTargetResponse } from '@opsopti/shared/src/services/api/api.types'
import { useAreFormQueriesFetched } from '@opsopti/shared/src/utils/tanstackHelpers'
import { observer } from 'mobx-react-lite'
import { createContext, Fragment, useContext, useEffect, useMemo } from 'react'
import type { z } from 'zod'

import { RadioCard } from '@/components/RadioCard'
import type { ScreenProps } from '@/components/Screen'
import { Screen } from '@/components/Screen'
import { SectionedForm, type SectionedFormProps } from '@/components/SectionedForm'
import type { RouteParams } from '@/configs/routes'
import {
  getFormScreenTitle,
  getFormSubmitLabel,
  showErrorNotification,
  showSuccessNotification,
} from '@/utils/componentCommons'
import { goBack } from '@/utils/navigation'

import { TableScreenDescription, tableScreenIcon } from './TableScreen'

type AssetSentryTargetFormScreenProps = {
  type?: 'screen' | 'drawer'
  params?: RouteParams['assetSentryTargetNew'] | RouteParams['assetSentryTargetEdit']
  goBackFn?: () => void
  onAfterSubmitFn?: (group: CreateAssetTargetResponse | UpdateAssetTargetResponse) => void
}

type FormSection = {
  id: string | null
  type: 'screen' | 'drawer'
  form: UseFormReturnType<z.infer<AssetTargetSchema>>
  isMutating: boolean
  submitFn: () => Promise<void>
  goBackFn: () => void
}

const AssetTargetFormDataContext = createContext<FormSection>({} as FormSection)
const useAssetTargetFormData = () => useContext(AssetTargetFormDataContext)

export const AssetSentryTargetFormScreen = observer(function AssetSentryTargetFormScreen(
  props: AssetSentryTargetFormScreenProps,
) {
  const { uiStore } = useGlobalState()

  const id = props.params?.id ?? null
  const isNew = useMemo(() => !id, [id])
  const type = props.type ?? 'screen'

  const { form } = useAssetTargetForm()

  const targetQuery = useAssetTargetQuery({ params: { id } })
  const targetCreateMutation = useAssetTargetCreateMutation({
    onErrorNotification: showErrorNotification,
    onSuccessNotification: showSuccessNotification,
  })
  const targetUpdateMutation = useAssetTargetUpdateMutation({
    onErrorNotification: showErrorNotification,
    onSuccessNotification: showSuccessNotification,
  })

  const areQueriesLoaded = useAreFormQueriesFetched([!isNew ? targetQuery.isFetched : null], 200)

  useEffect(() => {
    if (areQueriesLoaded) {
      form.initialize(castAssetTargetToForm(targetQuery.data))
      uiStore.hideLoadingOverlay()
    } else {
      uiStore.showLoadingOverlay()
    }
  }, [areQueriesLoaded])

  useEffect(() => uiStore.hideLoadingOverlay, [])

  useHotkeys([['mod+S', submitFn]], [])

  const goBackFn = props.goBackFn ?? goBack

  async function submitFn() {
    const validations = form.validate()

    if (validations.hasErrors) {
      showErrorNotification({ title: 'Form Errors', message: 'Please fill out all required fields before submitting.' })
      return
    }

    let response: CreateAssetTargetResponse | UpdateAssetTargetResponse | undefined

    if (isNew) {
      response = await targetCreateMutation.mutateAsync(form.getValues())
    } else {
      response = await targetUpdateMutation.mutateAsync({ ...form.getValues(), id: id! })
    }

    props.onAfterSubmitFn?.(response)

    goBackFn()
  }

  const sections = useMemo(
    () =>
      [
        {
          id: 'general',
          title: 'General Details',
          subtitle: 'Basic details about the target.',
          collapsible: false,
          valid: isFieldsListValid(form, ['name', 'type']),
          ContentComponent: GeneralFields,
        },

        {
          id: 'additional',
          title: 'Additional Information',
          subtitle: 'Visibility settings, notes, and other information.',
          collapsible: false,
          valid: isFieldsListValid(form, ['visibilityStatus']),
          ContentComponent: AdditionalFields,
        },
        {
          id: 'actions',
          collapsible: false,
          ContentComponent: FormActions,
        },
      ] satisfies SectionedFormProps['sections'],
    [form],
  )

  const isMutating = targetCreateMutation.isPending || targetUpdateMutation.isPending
  const FormWrapper = type === 'screen' ? Screen : Fragment
  const FormWrapperProps: ScreenProps =
    type === 'screen'
      ? {
          maw: 1440,
          title: getFormScreenTitle(id, 'Target', targetQuery.data?.name),
          icon: tableScreenIcon['targets'],
          description: <TableScreenDescription entity="targets" />,
        }
      : ({} as ScreenProps)

  return (
    <FormWrapper {...FormWrapperProps}>
      <AssetTargetFormDataContext.Provider value={{ id, form, submitFn, goBackFn, isMutating, type }}>
        <SectionedForm sections={sections} sectionsWithBorder={type === 'screen'} />
      </AssetTargetFormDataContext.Provider>
    </FormWrapper>
  )
})

function GeneralFields() {
  const props = useAssetTargetFormData()

  const RadioWrapper = props.type === 'screen' ? Group : Stack
  const RadioComponent = props.type === 'screen' ? RadioCard : Radio

  return (
    <Grid gutter={props.type === 'screen' ? 'xxl' : 'xl'}>
      <Grid.Col span={12}>
        <Radio.Group label="Target Type" withAsterisk {...props.form.getInputProps('type')}>
          <RadioWrapper align="stretch">
            <RadioComponent
              value={assetTargetSchema.shape.type.enum.team}
              label="Team"
              description="A shift or team that assets are assigned to."
            />

            <RadioComponent
              value={assetTargetSchema.shape.type.enum.location}
              label="Location"
              description="A physical location where assets are stored."
            />

            <RadioComponent
              value={assetTargetSchema.shape.type.enum.other}
              label="Other"
              description="Miscellaneous target type (e.g. asset condition, team, etc)."
            />
          </RadioWrapper>
        </Radio.Group>
      </Grid.Col>

      <Grid.Col span={12}>
        <TextInput
          withAsterisk
          label="Name"
          description="A unique name for the target."
          {...props.form.getInputProps('name')}
        />
      </Grid.Col>
    </Grid>
  )
}

function AdditionalFields() {
  const props = useAssetTargetFormData()

  const RadioWrapper = props.type === 'screen' ? Group : Stack
  const RadioComponent = props.type === 'screen' ? RadioCard : Radio

  return (
    <Grid gutter={props.type === 'screen' ? 'xxl' : 'xl'}>
      <Grid.Col span={12}>
        <Radio.Group label="Visibility" withAsterisk {...props.form.getInputProps('visibilityStatus')}>
          <RadioWrapper align="stretch">
            <RadioComponent
              value={assetTargetSchema.shape.visibilityStatus.enum.internal}
              label="Internal"
              description="Targets that are only visible to Supervisors and Admins."
            />

            <RadioComponent
              value={assetTargetSchema.shape.visibilityStatus.enum.public}
              label="Public"
              description="Targets that are visible to all users."
            />

            <RadioComponent
              value={assetTargetSchema.shape.visibilityStatus.enum.archived}
              label="Archived"
              description="Targets that are no longer in use."
            />
          </RadioWrapper>
        </Radio.Group>
      </Grid.Col>

      <Grid.Col span={12}>
        <Textarea
          label="Notes For Supervisors"
          autosize
          minRows={4}
          description="Notes that can be used for internal purposes. Only visible to Supervisors."
          {...props.form.getInputProps('internalNotes')}
        />
      </Grid.Col>

      <Grid.Col span={12}>
        <Textarea
          label="Notes For Members"
          autosize
          minRows={4}
          description="Notes displayed publicly to Members."
          {...props.form.getInputProps('memberNotes')}
        />
      </Grid.Col>
    </Grid>
  )
}
function FormActions() {
  const props = useAssetTargetFormData()

  return (
    <Stack gap="xxl">
      <Group gap="xl">
        <Button size="lg" radius="sm" onClick={props.submitFn} loading={props.isMutating}>
          {getFormSubmitLabel(props.id, 'Target')}
        </Button>

        {!props.isMutating && (
          <Button size="sm" radius="sm" variant="light" color="gray" onClick={props.goBackFn}>
            Cancel
          </Button>
        )}
      </Group>
    </Stack>
  )
}
